import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Loader2, User, Lock, Building2, Mail, ArrowLeft, Check, Shield, UserX, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

// Neutral marketing landing page opened for account sign-up (kept off any in-app
// purchase/checkout flow so the iOS app stays compliant with Apple's rules).
const SIGNUP_LANDING_URL = 'https://app.equiprcore.com/features';

// Login modes
const LOGIN_MODE = {
  CHECKING: 'checking',      // Checking device token
  PIN: 'pin',               // Quick PIN login (trusted device)
  EMAIL: 'email',           // Email + password login
  LEGACY: 'legacy',         // Legacy Client ID login
  PIN_SETUP: 'pin_setup',   // Setting up PIN after email login
  TENANT_SELECT: 'tenant_select',  // Selecting tenant (multi-tenant user)
  PASSWORD_CHANGE: 'password_change'  // Forced password change on first login
};

const MobileLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { 
    login,
    loginV2,
    loginWithPin,
    setupPin,
    switchAccount,
    selectTenant,
    checkDeviceToken,
    changePassword,
    operator, 
    deviceToken,
    trustedUserInfo,
    v2AuthEnabled,
    pendingPinSetup,
    pendingPasswordChange
  } = useMobileAuth();
  
  // UI State
  const [loginMode, setLoginMode] = useState(LOGIN_MODE.CHECKING);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // PIN state
  const [pin, setPin] = useState('');
  
  // Email login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Legacy login state
  const [clientId, setClientId] = useState('');
  const [operatorCode, setOperatorCode] = useState('');
  
  // Tenant selection state
  const [tenants, setTenants] = useState([]);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Redirect if already logged in (but not during password change or pin setup)
  useEffect(() => {
    if (operator && loginMode !== LOGIN_MODE.PASSWORD_CHANGE && loginMode !== LOGIN_MODE.PIN_SETUP) {
      // Check if password change is needed first (for page refresh scenarios)
      if (pendingPasswordChange && v2AuthEnabled) {
        setLoginMode(LOGIN_MODE.PASSWORD_CHANGE);
      } else if (pendingPinSetup && v2AuthEnabled) {
        setLoginMode(LOGIN_MODE.PIN_SETUP);
      } else {
        navigate('/mobile/dashboard');
      }
    }
  }, [operator, navigate, pendingPinSetup, pendingPasswordChange, v2AuthEnabled, loginMode]);

  // Check for trusted device on mount
  useEffect(() => {
    const checkTrustedDevice = async () => {
      if (!v2AuthEnabled) {
        // V2 not enabled, use legacy
        setLoginMode(LOGIN_MODE.LEGACY);
        return;
      }
      
      if (deviceToken) {
        const result = await checkDeviceToken();
        if (result.valid) {
          setLoginMode(LOGIN_MODE.PIN);
        } else {
          setLoginMode(LOGIN_MODE.EMAIL);
        }
      } else {
        setLoginMode(LOGIN_MODE.EMAIL);
      }
    };
    
    checkTrustedDevice();
  }, [v2AuthEnabled, deviceToken, checkDeviceToken]);

  // Handle PIN input
  const handlePinInput = useCallback((digit) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
      setError('');
    }
  }, [pin]);

  const handlePinBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handleClearPin = useCallback(() => {
    setPin('');
    setError('');
  }, []);

  // PIN Login handler
  const handlePinLogin = async () => {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await loginWithPin(pin);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/mobile/dashboard');
    } else {
      setError(result.error);
      setPin('');
      
      // If device is no longer valid, switch to email login
      if (result.deviceInvalid) {
        toast.error('Device trust expired. Please log in with email.');
        setLoginMode(LOGIN_MODE.EMAIL);
      }
    }
    
    setLoading(false);
  };

  // Email login handler
  const handleEmailLogin = async (e) => {
    e?.preventDefault();
    
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await loginV2(email, password);
    
    if (result.success) {
      if (result.requiresPasswordChange) {
        // User must change their password first
        // Store current password for the change request
        setCurrentPassword(password);
        // Set login mode BEFORE the useEffect can redirect
        setLoginMode(LOGIN_MODE.PASSWORD_CHANGE);
        toast.info('Please set a new password to continue');
        setLoading(false);
        return; // Don't proceed further
      }
      
      if (result.requiresTenantSelection && result.tenants?.length > 1) {
        setTenants(result.tenants);
        setLoginMode(LOGIN_MODE.TENANT_SELECT);
      } else if (result.requiresPinSetup) {
        setLoginMode(LOGIN_MODE.PIN_SETUP);
        toast.info('Set up a PIN for quick login next time');
      } else {
        toast.success('Welcome!');
        navigate('/mobile/dashboard');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Legacy login handler
  const handleLegacyLogin = async () => {
    if (!clientId.trim()) {
      setError('Please enter your Client ID');
      return;
    }
    if (!operatorCode.trim()) {
      setError('Please enter your Access Code');
      return;
    }
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await login(clientId, operatorCode, pin);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/mobile/dashboard');
    } else {
      setError(result.error);
      setPin('');
    }
    
    setLoading(false);
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await changePassword(currentPassword, newPassword);
    
    if (result.success) {
      toast.success('Password changed successfully!');
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Proceed to PIN setup - set mode directly here to avoid race conditions
      setLoginMode(LOGIN_MODE.PIN_SETUP);
      toast.info('Set up a PIN for quick login next time', { duration: 4000 });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // PIN setup handler
  const handlePinSetup = async () => {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await setupPin(pin);
    
    if (result.success) {
      toast.success('PIN set up successfully! You can use it next time.');
      navigate('/mobile/dashboard');
    } else {
      setError(result.error);
      setPin('');
    }
    
    setLoading(false);
  };

  // Skip PIN setup
  const handleSkipPinSetup = () => {
    navigate('/mobile/dashboard');
  };

  // Business owner sign-up. On iOS we must leave the app into the external browser
  // (Apple disallows in-app subscription sign-up); Android and web/PWA go in-app.
  const handleCreateAccount = () => {
    const platform = window.Capacitor?.getPlatform?.();
    if (platform === 'ios') {
      window.open(SIGNUP_LANDING_URL, '_system');
    } else {
      navigate('/free-trial');
    }
  };

  // Switch account handler
  const handleSwitchAccount = async () => {
    setLoading(true);
    await switchAccount();
    setLoginMode(LOGIN_MODE.EMAIL);
    setPin('');
    setError('');
    setLoading(false);
    toast.info('Device trust cleared. Please log in with email.');
  };

  // Toggle between email and legacy login
  const toggleLoginMode = () => {
    setError('');
    setPin('');
    if (loginMode === LOGIN_MODE.EMAIL) {
      setLoginMode(LOGIN_MODE.LEGACY);
    } else if (loginMode === LOGIN_MODE.LEGACY) {
      setLoginMode(LOGIN_MODE.EMAIL);
    }
  };

  // PIN Pad component
  const PinPad = ({ onSubmit, submitLabel = 'Sign In', submitDisabled = false }) => (
    <>
      {/* PIN Display */}
      <div className="space-y-1.5">
        <Label className="text-[var(--m-text-secondary)] text-xs uppercase flex items-center gap-1.5">
          <Lock className="h-3 w-3" />
          {loginMode === LOGIN_MODE.PIN_SETUP ? 'Create PIN' : 'Enter PIN'}
        </Label>
        <div className="flex justify-center gap-2 py-3 bg-[var(--m-bg)] rounded-md border border-[var(--m-border-light)]">
          {[0,1,2,3,4,5].map((i) => (
            <div
              key={`opt-${i}`}
              className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? 'bg-[#ffd108]' : 'bg-[var(--m-border-light)]'}`}
              data-testid={`pin-dot-${i}`}
            />
          ))}
        </div>
      </div>
      
      {error && (
        <p className="text-red-400 text-sm text-center bg-red-400/10 py-2 rounded-md" data-testid="login-error">
          {error}
        </p>
      )}
      
      {/* PIN Pad Grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {[1,2,3,4,5,6,7,8,9].map((digit) => (
          <Button
            key={digit}
            variant="outline"
            className="h-12 text-lg font-bold bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
            onClick={() => handlePinInput(String(digit))}
            disabled={loading}
            data-testid={`pin-digit-${digit}`}
          >
            {digit}
          </Button>
        ))}
        <Button
          variant="outline"
          className="h-12 text-xs bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
          onClick={handleClearPin}
          disabled={loading}
          data-testid="pin-clear"
        >
          Clear
        </Button>
        <Button
          variant="outline"
          className="h-12 text-lg font-bold bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
          onClick={() => handlePinInput('0')}
          disabled={loading}
          data-testid="pin-digit-0"
        >
          0
        </Button>
        <Button
          variant="outline"
          className="h-12 text-lg bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
          onClick={handlePinBackspace}
          disabled={loading}
          data-testid="pin-backspace"
        >
          ←
        </Button>
      </div>
      
      <Button 
        onClick={onSubmit} 
        className="w-full btn-volt mt-2"
        disabled={loading || pin.length < 4 || submitDisabled}
        data-testid="pin-submit-button"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
      </Button>
    </>
  );

  // Render loading state
  if (loginMode === LOGIN_MODE.CHECKING) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex flex-col items-center justify-center p-4 safe-top">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        <p className="text-[var(--m-text-secondary)] mt-4">Checking device...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--m-bg)] flex flex-col items-center justify-center p-4 safe-top">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link
            to="/super-admin/login"
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl overflow-hidden mb-3"
            data-testid="mobile-login-logo-link"
            aria-label="Super Admin login"
          >
            <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
          </Link>
          <h1 className="text-xl font-bold text-[var(--m-text)]">EQUIPR CORE</h1>
          <p className="text-[var(--m-text-secondary)] text-sm">Worker Portal</p>
        </div>

        {/* PIN Login (Trusted Device) */}
        {loginMode === LOGIN_MODE.PIN && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="pin-login-card">
            <CardHeader className="pb-3 text-center">
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-[#ffd108]" />
                </div>
              </div>
              <CardTitle className="text-[var(--m-text)] text-lg">
                Welcome back, {trustedUserInfo?.name?.split(' ')[0] || 'Worker'}
              </CardTitle>
              <CardDescription className="text-[var(--m-text-secondary)]">
                Enter your PIN to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PinPad onSubmit={handlePinLogin} />
              
              <div className="pt-3 border-t border-[var(--m-border)]">
                <button
                  onClick={handleSwitchAccount}
                  className="w-full flex items-center justify-center gap-2 text-[var(--m-text-secondary)] hover:text-[var(--m-text)] text-sm py-2"
                  disabled={loading}
                  data-testid="switch-account-button"
                >
                  <UserX className="h-4 w-4" />
                  Not {trustedUserInfo?.name?.split(' ')[0]}? Switch account
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Login */}
        {loginMode === LOGIN_MODE.EMAIL && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="email-login-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-[var(--m-text)] flex items-center gap-2 text-base">
                <Mail className="h-4 w-4 text-[#ffd108]" />
                Sign In with Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                    disabled={loading}
                    data-testid="mobile-email-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                    disabled={loading}
                    data-testid="mobile-password-input"
                  />
                </div>
                
                {error && (
                  <p className="text-red-400 text-sm text-center bg-red-400/10 py-2 rounded-md" data-testid="login-error">
                    {error}
                  </p>
                )}
                
                <Button 
                  type="submit"
                  className="w-full btn-volt"
                  disabled={loading || !email || !password}
                  data-testid="mobile-email-login-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
                </Button>

                <div className="text-center">
                  <Link
                    to="/mobile/forgot-password"
                    className="text-xs text-[var(--m-text-secondary)] hover:text-[var(--m-text)] inline-flex items-center gap-1"
                    data-testid="mobile-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>
              
              <div className="pt-3 border-t border-[var(--m-border)] space-y-2">
                <button
                  onClick={toggleLoginMode}
                  className="w-full flex items-center justify-center gap-2 text-[var(--m-text-secondary)] hover:text-[var(--m-text)] text-sm py-2"
                  data-testid="use-legacy-login"
                >
                  <Building2 className="h-4 w-4" />
                  Use Client ID & Access Code
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Legacy Login */}
        {loginMode === LOGIN_MODE.LEGACY && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="legacy-login-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-[var(--m-text)] flex items-center gap-2 text-base">
                <Lock className="h-4 w-4 text-[#ffd108]" />
                Sign In
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase flex items-center gap-1.5">
                  <Building2 className="h-3 w-3" />
                  Client ID
                </Label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value.toUpperCase())}
                  placeholder="e.g., TST001"
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-center tracking-widest"
                  disabled={loading}
                  data-testid="mobile-client-id-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[var(--m-text-secondary)] text-xs uppercase flex items-center gap-1.5">
                  <User className="h-3 w-3" />
                  Access Code
                </Label>
                <Input
                  value={operatorCode}
                  onChange={(e) => setOperatorCode(e.target.value.toUpperCase())}
                  placeholder="e.g., JH001"
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-center tracking-widest"
                  disabled={loading}
                  data-testid="mobile-access-code-input"
                />
              </div>

              <PinPad 
                onSubmit={handleLegacyLogin} 
                submitDisabled={!clientId || !operatorCode}
              />
              
              <div className="pt-3 border-t border-[var(--m-border)] space-y-2">
                {v2AuthEnabled && (
                  <button
                    onClick={toggleLoginMode}
                    className="w-full flex items-center justify-center gap-2 text-[var(--m-text-secondary)] hover:text-[var(--m-text)] text-sm py-2"
                    data-testid="use-email-login"
                  >
                    <Mail className="h-4 w-4" />
                    Sign in with Email
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Password Change (First Login) */}
        {loginMode === LOGIN_MODE.PASSWORD_CHANGE && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="password-change-card">
            <CardHeader className="pb-3 text-center">
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                  <KeyRound className="h-6 w-6 text-[#ffd108]" />
                </div>
              </div>
              <CardTitle className="text-[var(--m-text)] text-lg">
                Set Your Password
              </CardTitle>
              <CardDescription className="text-[var(--m-text-secondary)]">
                Create a new password to secure your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[var(--m-text-secondary)] text-xs uppercase">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                    disabled={loading}
                    autoFocus
                    data-testid="new-password-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-[var(--m-text-secondary)] text-xs uppercase">Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                    disabled={loading}
                    data-testid="confirm-password-input"
                  />
                </div>
                
                {/* Password requirements */}
                <div className="bg-[var(--m-bg)] rounded-lg p-3 space-y-2">
                  <p className="text-[var(--m-text-secondary)] text-xs uppercase font-medium">Password Requirements</p>
                  <div className="space-y-1">
                    <div className={`flex items-center gap-2 text-xs ${newPassword.length >= 8 ? 'text-green-400' : 'text-[var(--m-text-muted)]'}`}>
                      {newPassword.length >= 8 ? <Check className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-[var(--m-border-light)]" />}
                      At least 8 characters
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${newPassword && newPassword === confirmPassword ? 'text-green-400' : 'text-[var(--m-text-muted)]'}`}>
                      {newPassword && newPassword === confirmPassword ? <Check className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-[var(--m-border-light)]" />}
                      Passwords match
                    </div>
                  </div>
                </div>
                
                {error && (
                  <p className="text-red-400 text-sm text-center bg-red-400/10 py-2 rounded-md" data-testid="password-change-error">
                    {error}
                  </p>
                )}
                
                <Button 
                  type="submit"
                  className="w-full btn-volt"
                  disabled={loading || !newPassword || !confirmPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                  data-testid="set-password-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* PIN Setup */}
        {loginMode === LOGIN_MODE.PIN_SETUP && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="pin-setup-card">
            <CardHeader className="pb-3 text-center">
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <CardTitle className="text-[var(--m-text)] text-lg">Set Up Quick Login</CardTitle>
              <CardDescription className="text-[var(--m-text-secondary)]">
                Create a PIN to sign in faster next time
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PinPad onSubmit={handlePinSetup} submitLabel="Set PIN" />
              
              <button
                onClick={handleSkipPinSetup}
                className="w-full text-[var(--m-text-secondary)] hover:text-[var(--m-text)] text-sm py-2"
                disabled={loading}
                data-testid="skip-pin-setup"
              >
                Skip for now
              </button>
            </CardContent>
          </Card>
        )}

        {/* Tenant Selection */}
        {loginMode === LOGIN_MODE.TENANT_SELECT && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="tenant-select-card">
            <CardHeader className="pb-3 text-center">
              <CardTitle className="text-[var(--m-text)] text-lg">Select Organization</CardTitle>
              <CardDescription className="text-[var(--m-text-secondary)]">
                Choose which organization to access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {tenants.map((tenant) => (
                <button
                  key={tenant.client_id}
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (loading) return;
                    setLoading(true);
                    setError('');
                    try {
                      const result = await selectTenant(tenant.client_id);
                      if (result.success) {
                        toast.success(`Signed in to ${tenant.company_name}`);
                        setLoginMode(LOGIN_MODE.PIN_SETUP);
                      } else {
                        setError(result.error || 'Failed to select organisation');
                        toast.error(result.error || 'Failed to select organisation');
                      }
                    } catch (err) {
                      const msg = err?.message || 'Failed to select organisation';
                      setError(msg);
                      toast.error(msg);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
                  className="w-full p-4 rounded-lg border border-[var(--m-border-light)] active:border-[#ffd108] active:bg-[var(--m-bg-elevated)]/50 md:hover:border-[#ffd108] md:hover:bg-[var(--m-bg-elevated)]/50 transition-colors text-left cursor-pointer select-none"
                  data-testid={`tenant-option-${tenant.client_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--m-bg-elevated)] flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[var(--m-text-secondary)]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[var(--m-text)]">{tenant.company_name}</div>
                      <div className="text-xs text-[var(--m-text-secondary)]">{tenant.client_id}</div>
                    </div>
                  </div>
                </button>
              ))}
              
              <div className="pt-3 border-t border-[var(--m-border)]">
                <button
                  onClick={() => {
                    setLoginMode(LOGIN_MODE.EMAIL);
                    setTenants([]);
                  }}
                  className="w-full flex items-center justify-center gap-2 text-[var(--m-text-secondary)] hover:text-[var(--m-text)] text-sm py-2"
                  data-testid="back-to-login"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </button>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Business owner sign-up. iOS opens the external browser to a neutral page;
            Android and web/PWA go in-app to the free-trial flow. */}
        {(loginMode === LOGIN_MODE.EMAIL || loginMode === LOGIN_MODE.LEGACY) && (
          <div className="mt-5 text-center" data-testid="mobile-create-account-section">
            <p className="text-[var(--m-text-muted)] text-xs mb-1.5">Setting up your business?</p>
            <button
              onClick={handleCreateAccount}
              className="text-[#ffd108] hover:text-[#ffdb4d] text-sm font-medium inline-flex items-center justify-center gap-1.5"
              data-testid="mobile-create-account-button"
            >
              <Building2 className="h-4 w-4" />
              Create an account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileLogin;
